import { useEffect, useState } from "react";
import type { Session } from "../app/store.ts";
import { Alert, Badge, Icon, Skeleton } from "../components/ui/index.tsx";
import { ControlsGrid } from "../hud/ControlsGrid.tsx";
import { fmt, fmtCount, t } from "../i18n/index.ts";
import type { WaitingView } from "../net/game-client.ts";
import { clockFromSeconds } from "../utils/format.ts";
import { errorText } from "./Lobby.tsx";

const BUILD_ERRORS = new Set(["protocol", "sim_hash", "wire_format"]);

/**
 * The room before the fight. A practice room starts the moment its player is
 * in, so it only ever shows "stepping in"; a custom session shows who is in,
 * what it needs, and the host's start button.
 */
export function WaitingRoom({
  waiting,
  session,
  onStart,
  onLeave,
}: {
  waiting: WaitingView | null;
  session: Session;
  onStart: () => void;
  onLeave: () => void;
}) {
  const now = useSecondTicker();
  const info = waiting?.info ?? null;

  if (waiting?.closed) {
    const code = waiting.error;
    const text =
      code && BUILD_ERRORS.has(code)
        ? t.match.buildMismatch
        : code === "expired"
          ? t.lobby.errors.expired
          : code && code in t.lobby.errors
            ? errorText(code)
            : waiting.info
              ? t.waiting.closed
              : t.waiting.lost;
    return (
      <div className="card pause-card stack stack-4">
        <h2>{t.waiting.title}</h2>
        <Alert kind="danger">{text}</Alert>
        <button type="button" className="btn btn-primary btn-lg" onClick={onLeave}>
          {t.waiting.back}
        </button>
      </div>
    );
  }

  if (!info || info.mode === "practice") {
    return (
      <div className="card pause-card stack stack-4" role="status" aria-busy="true">
        <h2>{t.lobby.connecting}</h2>
        <Skeleton height={20} width="60%" />
        <Skeleton height={44} />
        <button type="button" className="btn" onClick={onLeave}>
          <Icon name="exit" />
          {t.waiting.leave}
        </button>
      </div>
    );
  }

  const host = info.players.find((p) => p.host);
  const missing = Math.max(0, info.minHumans - info.players.length);
  const closesIn = info.closesIn - (now - (waiting?.infoAt ?? now)) / 1000;
  const status =
    missing > 0
      ? fmtCount(t.waiting.moreNeeded, missing)
      : info.youAreHost
        ? t.waiting.readyHost
        : fmt(t.waiting.readyGuest, { host: host?.nickname ?? "" });

  return (
    <div className="card pause-card stack stack-4">
      <div className="pause-title">
        <h2>{t.waiting.title}</h2>
        <p className="muted">{fmt(t.waiting.session, { name: host?.nickname ?? "" })}</p>
      </div>

      <div className="waiting-facts">
        <Badge>
          <Icon name="users" />
          {fmt(t.waiting.needs, { min: info.minHumans })}
        </Badge>
        <Badge>
          <Icon name="bot" />
          {info.bots > 0 ? fmtCount(t.waiting.bots, info.bots) : t.waiting.noBots}
        </Badge>
        {info.locked ? (
          <Badge kind="accent">
            <Icon name="lock" />
            {session.pin ? fmt(t.waiting.pin, { pin: session.pin }) : t.waiting.locked}
          </Badge>
        ) : null}
      </div>

      <div className="pause-section">
        {t.waiting.players}{" "}
        <span className="num">
          {info.players.length}/{info.minHumans}
        </span>
      </div>
      <ul className="waiting-list">
        {info.players.map((p, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: nicknames may repeat; arrival order is the identity
          <li key={i} className={p.you ? "is-me" : undefined}>
            <span className="waiting-name">{p.nickname}</span>
            {p.host ? <Badge kind="accent">{t.waiting.host}</Badge> : null}
            {p.you ? <Badge>{t.waiting.you}</Badge> : null}
          </li>
        ))}
      </ul>

      <p className={`waiting-status${missing === 0 ? " is-ready" : ""}`} role="status">
        {status}
      </p>
      {waiting?.error === "not_ready" ? <Alert kind="danger">{t.waiting.notReady}</Alert> : null}
      <p className="muted num waiting-expiry">
        {fmt(t.waiting.closesIn, { time: clockFromSeconds(closesIn) })}
      </p>

      <div className="waiting-actions">
        <button type="button" className="btn" onClick={onLeave}>
          <Icon name="exit" />
          {t.waiting.leave}
        </button>
        {info.youAreHost ? (
          <button
            type="button"
            className="btn btn-primary"
            disabled={!info.canStart}
            onClick={onStart}
          >
            <Icon name="fist" />
            {t.waiting.start}
          </button>
        ) : null}
      </div>

      <div className="pause-section">{t.match.controlsTitle}</div>
      <ControlsGrid />
    </div>
  );
}

/** performance.now(), refreshed once a second, for the expiry countdown. */
function useSecondTicker(): number {
  const [now, setNow] = useState(() => performance.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(performance.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
}
